import { useEffect, useState } from 'react';
import { useHotkeys } from '../../components/hotkeys';

/**
 * "Minimize the Ribbon" for the SDMS screen — the Windows SDMS builder's ribbon option (user request 2026-10-22):
 * the ribbon collapses to its tab row, giving the sheet the room. Toggled by the arrow at the right of the tab row,
 * by double-clicking the SDMS tab, or with Ctrl+F1 (the Office / DevExpress key). While minimized a single click on
 * the SDMS tab shows the ribbon for one use; a click in the sheet (or Esc) hides it again, as in Office.
 * The choice is remembered per browser — a viewer convenience, so plain localStorage, guarded.
 */
const STORAGE_KEY = 'alpaca.sdms.ribbonMinimized';

export function readRibbonMinimized(): boolean {
  try { return localStorage.getItem(STORAGE_KEY) === '1'; } catch { return false; }   // storage blocked → expanded
}

export function writeRibbonMinimized(v: boolean): void {
  try { localStorage.setItem(STORAGE_KEY, v ? '1' : '0'); } catch { /* storage blocked — not remembered, still works */ }
}

export interface SdmsRibbonMinimize {
  /** The ribbon is collapsed to its tab row. */
  minimized: boolean;
  /** The ribbon is on screen: expanded, or minimized and shown for one use. */
  ribbonVisible: boolean;
  toggle: () => void;
  /** Single click on the SDMS tab — while minimized, show / hide the ribbon for one use. */
  onTabClick: () => void;
  /** Close a one-use ribbon (a click in the sheet). */
  closePeek: () => void;
}

export function useSdmsRibbonMinimize(): SdmsRibbonMinimize {
  const [minimized, setMinimized] = useState<boolean>(readRibbonMinimized);
  const [peek, setPeek] = useState(false);

  const toggle = () => {
    setPeek(false);
    setMinimized(m => { writeRibbonMinimized(!m); return !m; });
  };

  // Both through the ONE shared hotkey layer (standing rule 14). Esc only acts while a one-use ribbon is open
  // (disabled otherwise, so it falls through to any other Esc binding).
  useHotkeys([
    { combo: 'mod+f1', label: 'Minimize / expand the ribbon', group: 'SDMS', run: toggle, allowInInput: true },
    { combo: 'esc', label: 'Hide the ribbon', group: 'SDMS', run: () => setPeek(false), disabled: !peek, hidden: true },
  ]);

  // Leaving minimized mode ends any one-use ribbon.
  useEffect(() => { if (!minimized) setPeek(false); }, [minimized]);

  return {
    minimized,
    ribbonVisible: !minimized || peek,
    toggle,
    onTabClick: () => { if (minimized) setPeek(p => !p); },
    closePeek: () => setPeek(false),
  };
}
